import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { UploadImage, UploadResponse } from '../../../connection/upload-image';
import { SelectGroupOption } from '../../general-components/select-group';
import * as ModalActions from '../../../stores/actions/modal';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import * as Alerts from '../../general-components/alert-service';
import WaitingIndicator from '../../general-components/waiting-indicator';
import { GreenroomState as GS } from '../../../stores/types/types';
import LoadingSpinner from '../../general-components/loading-spinner';
import useValidateRtmp from '../../../hooks/use-validate-rtmp';
import { IMG_TYPES } from '../../../types/constants';
import { AddDestination } from '../../../connection/broadcasting';
import { DESTINATION_TYPE } from '../../../stores/types/broadcasting';
import { DestinationDiv, DestinationInput, CopyButton, ShowButton } from '../../broadcasting/settings/destination-card';
import styled from 'styled-components';

interface Props {
	destinationType: SelectGroupOption;
}

const DestinationDivWide = styled(DestinationDiv)`
	margin-bottom: 15px;
	width: 100%;
	background-color: #1A1A1F;
	outline: none;
	border: none;
	:hover {
		border: none;
	}
`;

export default function StreamDestinationSettings(props: Props) {
	const dispatch = useDispatch();
	const {
		destinationType,
	} = props;

	const token = useSelector((state: GS) => state.SessionReducer.token);
	const homeMeeting = useSelector((state: GS) => state.HomeReducer.homeMeeting);

	const [titleInput, setTitleInput] = useState<string>('');
	const [addressInput, setAddressInput] = useState<string>('');
	const [keyInput, setKeyInput] = useState<string>('');
	const [logoInput, setLogoInput] = useState<string>('');
	const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
	const [revealStreamKey, setRevealStreamKey] = useState<boolean>(false);

	const logoImageInput = useRef<HTMLInputElement | null>(null);

	const enableRevealStreamKey = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		
		setRevealStreamKey(true);
		setTimeout(() => {
			setRevealStreamKey(false);
		}, 5000);
	}

	const copyStreamKey = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		navigator.clipboard.writeText(addressInput.replace(/\s+/g, ''));
	}

	const {
		isValidatingRtmpEndpoint,
		rtmpOverrideModal,
		validateRtmpEndpoint,
	} = useValidateRtmp();

	function copyInput(e: React.MouseEvent<HTMLButtonElement>, address: boolean) {
		e.preventDefault();
		navigator.clipboard.writeText(address ? addressInput : keyInput);
	}

	async function postRtmpValidation(updateInputAddress: string) {
		if (!token || !homeMeeting) return;
		const isOtherType = destinationType?.value === DESTINATION_TYPE.OTHER;
		await AddDestination(
			token,
			homeMeeting.meeting,
			{
				name: isOtherType ? titleInput : destinationType?.label,
				url: updateInputAddress,
				streamKey: keyInput,
				logo: isOtherType ? logoInput : '',
				type: destinationType?.value,
				selected: false,
			},
		);
		dispatch(ModalActions.setModalOpen(false));
	}

	async function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		const updateInputAddress = addressInput.replace(/\s+/g, '');

		await validateRtmpEndpoint(
			destinationType?.value === DESTINATION_TYPE.OTHER
				? titleInput
				: destinationType?.label ?? '',
			updateInputAddress,
			keyInput,
			3,
			() => postRtmpValidation(updateInputAddress),
		);
	}

	function cancelForm(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();
		dispatch(ModalActions.setModalOpen(false));
	}

	function pickLogoImage(e: React.MouseEvent<HTMLDivElement>) {
		e.preventDefault();
		logoImageInput.current?.click();
	}

	function logoImageChange(element: React.ChangeEvent<HTMLInputElement>) {
		element.preventDefault();
		setUploadingLogo(true);
		if (element.target.files) {
			UploadImage(token, element.target.files).then((res: UploadResponse) => {
				setUploadingLogo(false);
				setLogoInput(res.url);
			}).catch((error) => {
				console.error(error);
				setUploadingLogo(false);
				Alerts.showAlert({
					message: 'Error uploading logo',
					description: 'We ran into an issue uploading that image. Please try again.',
					duration: 4000,
				});
			});
		} else {
			setUploadingLogo(false);
		}
	}

	return (
		<div className={classNames('info-modal', { other: destinationType?.value === DESTINATION_TYPE.OTHER })}>
			<div className="info-modal-header destination margin-bottom-medium">
				{
					destinationType?.value !== DESTINATION_TYPE.OTHER
					&& <img
						src={destinationType?.description}
						alt={destinationType?.description}
					/>
				}
				<h1>{
					destinationType?.value === DESTINATION_TYPE.OTHER
						? 'Create Destination'
						: `Add to ${destinationType?.label}`
				}</h1>
			</div>
			<form onSubmit={submitForm}>
				<div className="-body">
					{destinationType?.value === DESTINATION_TYPE.OTHER && (
						<>
							<div className="destination-modal-logo" onClick={pickLogoImage}>
								{uploadingLogo && <WaitingIndicator />}
								{!uploadingLogo && (
									!logoInput ? (
										<>
											<Icon name={ICONS.ADD} color={COLORS.WHITE} size={16} />
											Logo
										</>
									) : (<img src={logoInput} alt={logoInput} />)
								)}
							</div>
							<input
								style={{ display: 'none' }}
								type="file"
								accept={IMG_TYPES
									.map((a) => `.${a}`)
									.join(',')
								}
								ref={logoImageInput}
								onChange={logoImageChange}
							/>
						</>
					)}
					<div className={classNames('destination-inputs', { other: destinationType?.value === DESTINATION_TYPE.OTHER })}>
						{destinationType?.value === DESTINATION_TYPE.OTHER && <div className="input-container">
							<div className="input-header">
								<span>Title*</span>
							</div>
							<div className="input-body">
								<input type="text" onChange={({ target }) => setTitleInput(target.value)} value={titleInput} placeholder="Name your destination..." />
							</div>
						</div>}
						<div className="input-container">
							<div className="input-header">
								<span>RTMP address*</span>
							</div>
							<DestinationDivWide className="">
								<DestinationInput type="text" onChange={({ target }) => setAddressInput(target.value)} value={addressInput} placeholder="Paste RTMP address here..." />
								<CopyButton className="copy-button" onClick={(e) => copyStreamKey(e)}>
									<Icon name={ICONS.COPY_FILLED} color={COLORS.TEXT_GRAY_400} size={14} />
								</CopyButton>
							</DestinationDivWide>
						</div>
						<div className="input-container">
							<div className="input-header">
								<span>Stream key*</span>
							</div>
							<DestinationDivWide className="">
								<ShowButton className="show-button" onClick={(e) => enableRevealStreamKey(e)}>
									<Icon name={ICONS.EYE_SHOWN} color={COLORS.TEXT_GRAY_400} size={14} />
								</ShowButton>
								<DestinationInput type={revealStreamKey ? "text" : "password"} onChange={({ target }) => setKeyInput(target.value)} value={keyInput} placeholder="Paste stream key here..." />
								<CopyButton className="copy-button" onClick={(e) => copyInput(e, false)}>
									<Icon name={ICONS.COPY_FILLED} color={COLORS.WHITE} size={14} />
								</CopyButton>
							</DestinationDivWide>
						</div>
					</div>
				</div>
				<div className="info-modal-bottom">
					<div className="info-modal-buttons">
						<button className="info-gray" onClick={(e) => cancelForm(e)} disabled={false}>
							Cancel
						</button>
						<button
							className="info-green transition-opacity"
							disabled={isValidatingRtmpEndpoint}
						>
							{isValidatingRtmpEndpoint && (
								<>
									<LoadingSpinner
										height={10}
										width={10}
										color="#09090A"
										stroke={10}
										style={{ display: 'flex', margin: 'none' }}
									/>
									&nbsp;
								</>
							)}
							Save
						</button>
					</div>
				</div>
				{rtmpOverrideModal}
			</form>
		</div>
	);
}
